/**
 * THE PANEL — the vocabulary half.
 *
 * ATTORNEY_BRIEF Part 1B: a small number of independent Attorneys-at-Law register
 * per land-registry area; the client is shown NOTHING about them and cannot
 * choose; each case is assigned mechanically among the attorneys registered for
 * that parcel's registry area, with no person at the platform selecting.
 *
 * TODO-BLOCKED-Q1E. Everything here presumes ATTORNEY_BRIEF Q1(e) — that an
 * Attorney-at-Law may accept referrals from a panel operated by a fee-charging
 * non-attorney company — is answered yes. It is unanswered. This file builds the
 * mechanism; it does not authorise the business model.
 *
 * WHAT IS NOT HERE, AND MUST NEVER BE. No fee, price, rate or payout of any kind
 * (D1 is open — CLAUDE.md non-negotiable 3), and nothing that ranks, rates,
 * features or orders panel members for display. COMPLIANCE Constraint 1 (Rule 39,
 * touting) is the heart of this feature: the panel is not a directory, and no
 * client-facing surface may list, name or compare its members. A client learns
 * the name of the attorney on THEIR OWN case and nothing else.
 */
import { z } from "zod";
/**
 * Why a membership ENDED. Codes, not prose, for the reason every reason code in
 * this codebase is a code: the decision reaches the append-only audit log, which
 * can never be erased and therefore must never carry personal data (COMPLIANCE
 * Constraint 4). A code says what happened without naming anyone.
 *
 * Mirrored as a CHECK constraint in migration 0023 (`panel_departure_reason_is_known`).
 * Deliberate duplication, the same way the case machine and the verification
 * graph are duplicated: TypeScript fails early with a message naming the field,
 * the database fails closed on every path forever. IF THEY DRIFT, THE DATABASE
 * WINS — so a code added here without the migration is refused at the write, not
 * silently stored.
 */
export const PANEL_DEPARTURE_REASONS = [
    /** They asked to come off the panel. */
    "AT_PROFESSIONALS_REQUEST",
    /**
     * Written by the database, not by a person: revoking or rejecting a
     * verification ends every live membership in the same statement (migration
     * 0023's cascade). `left_by` is deliberately NULL on those rows — no admin
     * performed this removal, the verification decision caused it, and that
     * decision's own audit entry names the actor.
     */
    "VERIFICATION_ENDED",
    /**
     * Also written by the database, not by a person: erasing an account ends every
     * live seat it holds, in the same statement (migration 0026's cascade). Without
     * that, an already-seated professional who was later erased kept their seat,
     * stayed in the pool the rotation computes over, and was handed cases stamped
     * `panel_rotation` — the platform routing client land data to someone it had
     * undertaken to stop holding data about. `left_by` is NULL for the same reason
     * it is on VERIFICATION_ENDED: no admin performed this removal.
     *
     * A SEPARATE CODE FROM VERIFICATION_ENDED, deliberately. The verification was
     * not ended; the account was erased. Recording the wrong cause is a fabricated
     * fact (CLAUDE.md non-negotiable 1), so the two causes keep two names.
     */
    "ACCOUNT_ERASED",
    /**
     * THE PLATFORM'S OWN DECISION TO STOP SENDING THIS PERSON WORK.
     *
     * Same meaning, and the same warning, as WITHDRAWN_BY_PLATFORM in
     * ./professional.ts: it asserts NOTHING about the professional's enrolment and
     * NOTHING about their conduct. Their standing as an Attorney-at-Law is
     * untouched, is not ours to touch, and a finding of professional misconduct is
     * for the Bar Association of Sri Lanka and the Supreme Court alone. Any surface
     * rendering this code must never show it as, or summarise it as, a sanction.
     */
    "WITHDRAWN_BY_PLATFORM",
    "OTHER",
];
/**
 * Why a membership is PAUSED — a member who is temporarily not receiving new
 * cases, without losing their place or their history.
 *
 * READ THIS BEFORE ADDING A CODE. Pausing is the most steering-shaped lever in
 * the whole panel design: pause everybody but one and you have hand-picked the
 * attorney, which is precisely the act COMPLIANCE Constraint 1 exists to prevent
 * and precisely what mechanical assignment is for. That is why availability is
 * not a plain column grant but a governed act (tv_set_panel_availability, 0023),
 * why every pause and resume is audited with an actor and a reason, and why this
 * list stays short and describes the MEMBER'S availability rather than the
 * platform's preference. A code that means "we would rather send this elsewhere"
 * does not belong here; that is WITHDRAWN_BY_PLATFORM, which is a departure and
 * is visible as one.
 */
export const PANEL_PAUSE_REASONS = [
    /** They asked to stop receiving new cases for now. */
    "AT_PROFESSIONALS_REQUEST",
    /** Leave, illness, capacity — a stated period of unavailability. */
    "UNAVAILABLE_TEMPORARILY",
    "OTHER",
];
/**
 * Maximum length of `panel_memberships.source` — what the seating was based on
 * ("invited 2026-07, accepted by email"). Matches the registry-office provenance
 * field it is modelled on (CLAUDE.md §2A names `registry_offices` as the
 * provenance template) and the CHECK in migration 0023.
 */
export const PANEL_SOURCE_MAX_LENGTH = 300;
/**
 * The departure reasons AN OPERATOR MAY CHOOSE — the full list minus the two the
 * database writes for itself.
 *
 * `VERIFICATION_ENDED` (migration 0023) and `ACCOUNT_ERASED` (migration 0026) are
 * each written only by their own cascade, where each is true by construction, and
 * `tv_end_panel_membership()` refuses both from a human caller. A person selecting
 * one would be recording a cause that did not occur, which is the same fabrication
 * as any other invented fact (CLAUDE.md non-negotiable 1). So the admin form is
 * built from THIS list and the seat cannot be ended under a reason nobody caused.
 */
export const PANEL_OPERATOR_DEPARTURE_REASONS = PANEL_DEPARTURE_REASONS.filter((code) => code !== "VERIFICATION_ENDED" && code !== "ACCOUNT_ERASED");
/**
 * THE ONE PANEL-SEAT VALIDATION, shared by every door (CLAUDE.md §2A: "the same
 * zod/schema validation at the admin form and the importer as everywhere else").
 * The web form, the API route and the bulk importer all parse against this exact
 * schema; there is no second, laxer path for operators.
 *
 * Provenance is deliberately NOT in this schema, exactly as it is not in
 * `registryOfficeInputSchema`. `sourced_by` (the admin) and `sourced_at` are set
 * server-side from the authenticated principal and are never accepted from the
 * form or the import file — "who seated this member" must be the actual actor,
 * not a value the actor can type, and the database refuses a seat whose
 * `sourced_by` is not a live admin or is the member themselves. `source` (what
 * the seating was based on) IS operator-provided and required.
 *
 * NOTE WHAT CANNOT BE EXPRESSED HERE. There is no field for a position, an order,
 * a share, a capacity or a preference — and no fee, price, rate or payout of any
 * kind (D1 is open, CLAUDE.md non-negotiable 3). A seat says one thing: this
 * verified Attorney-at-Law is in the pool for this registry. Everything about
 * WHOSE TURN IT IS is derived at the moment of assignment from stored facts, so
 * there is no stored ranking for anyone to write, display or tamper with
 * (COMPLIANCE Constraint 1 — the panel is not a directory).
 */
export const panelMembershipInputSchema = z.object({
    /**
     * The registry office this seat is for. Same shape as
     * `registryOfficeInputSchema.code`, because it IS that key — the FK the
     * rotation matches `cases.registry_district` against.
     */
    registryOfficeCode: z
        .string()
        .trim()
        .min(1)
        .max(32)
        .regex(/^[A-Z0-9][A-Z0-9/-]*$/, "registry office code must be uppercase letters, digits, hyphens or slashes"),
    /**
     * The USER id, not the professional profile id — `assignments.assignee_user_id`
     * and `cases.assigned_professional_id` are both user ids, so keying the seat the
     * same way removes a translation step at the moment of assignment.
     */
    professionalUserId: z.string().trim().uuid("professionalUserId must be a user id (UUID)"),
    /**
     * What the seating was based on: "invited 2026-07, accepted by email". Required
     * and non-empty — the honesty guarantee that no seat enters the pool without a
     * stated origin. Refused here, and independently by the database's NOT NULL and
     * `panel_source_present` CHECK.
     */
    source: z.string().trim().min(1).max(PANEL_SOURCE_MAX_LENGTH),
});
/**
 * Pausing or resuming a seat. A discriminated union rather than an optional
 * reason, so "paused with no reason given" is unrepresentable at the type level
 * as well as refused by `tv_set_panel_availability()`.
 *
 * Resuming carries no reason on purpose: it restores the default state, and there
 * is nothing to justify. Pausing is the act that needs an author and a cause —
 * see the warning on PANEL_PAUSE_REASONS above.
 */
export const panelAvailabilityInputSchema = z.discriminatedUnion("paused", [
    z.object({ paused: z.literal(true), reason: z.enum(PANEL_PAUSE_REASONS) }),
    z.object({ paused: z.literal(false) }),
]);
/** Ending a seat. One-way; rejoining is a new row. */
export const panelDepartureInputSchema = z.object({
    reason: z.enum(PANEL_OPERATOR_DEPARTURE_REASONS),
});
//# sourceMappingURL=panel.js.map